import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { OverviewData } from '../models/overview-year.model';
import { TopChartTrack } from '../models/top-chart-track.model';
import { DashboardKpis } from '../models/dashboard-kpis.model';
import { ArtistsMarketBreadth } from '../models/market-breadth.model';
import { DateRange } from '../components/date-range-selector/date-range-selector.component';
import { TickerItem } from '../models/ticker-item.model';

interface TopChartsResponse {
  range: DateRange;
  tracks: TopChartTrack[];
}

interface DashboardKpisResponse extends DashboardKpis {
  range: DateRange;
}

interface TickerResponseItem {
  metric: string;
  label: string;
  value: number;
  formattedValue: string;
  change?: { direction: 'up' | 'down'; percent: number };
}

interface TickerResponse {
  range: DateRange;
  items: TickerResponseItem[];
}

@Injectable({
  providedIn: 'root'
})
export class OverviewService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/overview`;

  getYearOverview(year?: number): Observable<OverviewData> {
    const url = year ? `${this.apiUrl}/year?year=${year}` : `${this.apiUrl}/year`;
    return this.http.get<{ status: string; data: OverviewData }>(url).pipe(
      map(response => response.data)
    );
  }

  getTopCharts(range: DateRange = '7d'): Observable<TopChartTrack[]> {
    return this.http.get<{ status: string; data: TopChartsResponse }>(`${this.apiUrl}/top-charts?range=${range}`).pipe(
      map(response => response.data.tracks)
    );
  }

  getDashboardKpis(range: DateRange = '7d'): Observable<DashboardKpis> {
    return this.http.get<{ status: string; data: DashboardKpisResponse }>(`${this.apiUrl}/kpis?range=${range}`).pipe(
      map(response => response.data)
    );
  }

  getMarketBreadth(range: DateRange = '7d', startDate?: string, endDate?: string): Observable<ArtistsMarketBreadth> {
    let url = `${this.apiUrl}/market-breadth?range=${range}`;
    if (startDate && endDate) {
      url += `&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    }
    return this.http.get<{ status: string; data: ArtistsMarketBreadth }>(url).pipe(
      map(response => response.data)
    );
  }

  getTicker(range: DateRange = '7d', startDate?: string, endDate?: string): Observable<TickerItem[]> {
    let url = `${this.apiUrl}/ticker?range=${range}`;
    if (startDate && endDate) {
      url += `&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    }
    return this.http.get<{ status: string; data: TickerResponse }>(url).pipe(
      map(response => response.data.items.map(item => ({
        label: item.label,
        value: item.formattedValue,
        change: item.change
      })))
    );
  }
}
